import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Bell, Warning, FileText, X, Package, Wrench } from "@phosphor-icons/react";
import { formatDate, formatDateTime } from "@/lib/format";

const SEVERITY_STYLES = {
    critical: "bg-rose-50 border-rose-200 text-rose-700",
    warning: "bg-amber-50 border-amber-200 text-amber-700",
    info: "bg-blue-50 border-blue-200 text-blue-700",
};

const KIND_ICONS = {
    compliance_document: FileText,
    driver_license: FileText,
    hazardous_license: Warning,
    low_stock: Package,
    pm_due: Wrench,
};

export default function NotificationsBell() {
    const [open, setOpen] = useState(false);
    const [data, setData] = useState({ count: 0, items: [] });
    const ref = useRef(null);

    const load = async () => {
        try {
            const r = await api.get("/notifications?days=30");
            setData(r.data);
        } catch (e) {
            // Silent: the bell auto-polls every 60s. Log so failures are diagnosable
            // without spamming a toast to the user.
            console.warn("Notifications poll failed:", e?.response?.status || e?.message);
        }
    };

    useEffect(() => {
        load();
        const t = setInterval(load, 60000);
        return () => clearInterval(t);
    }, []);

    useEffect(() => {
        const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
        document.addEventListener("mousedown", onClick);
        return () => document.removeEventListener("mousedown", onClick);
    }, []);

    const markRead = async (it, e) => {
        if (it.kind !== "low_stock" && it.kind !== "pm_due") return;
        e?.preventDefault();
        e?.stopPropagation();
        try {
            await api.post(`/notifications/${it.id}/read`);
            load();
        } catch (e) {
            console.warn("Mark notification read failed:", e);
        }
    };

    const clearAll = async () => {
        try {
            // Snapshot everything currently visible so expiry-based items can
            // return automatically when the underlying document is renewed.
            const items = (data.items || []).map((it) => ({
                id: it.id,
                signature: it.expiry_date || it.created_at || "",
            }));
            await api.post("/notifications/clear-all", { items });
            // Optimistically clear so the UI feels instant; the next poll
            // will reconcile with the backend state.
            setData({ count: 0, items: [] });
            setOpen(false);
            load();
        } catch (e) {
            console.warn("Clear-all failed:", e);
        }
    };

    const critical = data.items.filter((i) => i.severity === "critical").length;
    const warning = data.items.filter((i) => i.severity === "warning").length;

    return (
        <div className="relative" ref={ref}>
            <button
                onClick={() => setOpen((o) => !o)}
                className="text-fleet-inkSoft hover:text-fleet-ink relative"
                data-testid="notifications-btn"
                aria-label="Notifications"
            >
                <Bell size={20} weight={data.count > 0 ? "fill" : "regular"} />
                {data.count > 0 && (
                    <span className={`absolute -top-1 -right-1 min-w-[16px] h-4 rounded-full text-[10px] font-mono font-semibold flex items-center justify-center px-1 ${critical > 0 ? "bg-fleet-critical text-white" : "bg-fleet-warning text-white"}`}>
                        {data.count}
                    </span>
                )}
            </button>

            {open && (
                <div className="absolute right-0 top-10 w-[28rem] bg-white border border-fleet-border rounded-md z-50 max-h-[70vh] flex flex-col animate-fade-in">
                    <div className="px-4 py-3 border-b border-fleet-border flex items-center justify-between">
                        <div>
                            <div className="font-display font-semibold text-sm">Alerts & Renewals</div>
                            <div className="label-mono text-[10px] mt-0.5">
                                {critical > 0 && <span className="text-fleet-critical">{critical} CRITICAL · </span>}
                                {warning > 0 && <span className="text-fleet-warning">{warning} WARNING · </span>}
                                {data.count} total
                            </div>
                        </div>
                        <div className="flex items-center gap-2">
                            <button onClick={clearAll}
                                className="text-xs text-fleet-inkSoft hover:text-fleet-ink underline disabled:opacity-40 disabled:no-underline disabled:cursor-not-allowed"
                                disabled={data.count === 0}
                                data-testid="clear-all-notifications">
                                Clear all
                            </button>
                            <button onClick={() => setOpen(false)} className="text-fleet-inkSoft hover:text-fleet-ink" aria-label="Close"><X size={16} /></button>
                        </div>
                    </div>

                    <div className="overflow-y-auto flex-1 divide-y divide-fleet-border">
                        {data.items.length === 0 ? (
                            <div className="text-center py-10 px-6 text-sm text-fleet-inkSoft">No alerts. All clear.</div>
                        ) : (
                            data.items.map((it) => {
                                const Icon = KIND_ICONS[it.kind] || (it.severity === "critical" ? Warning : FileText);
                                const isAlert = it.kind === "low_stock" || it.kind === "pm_due";
                                const target = it.link
                                    || (it.driver_id ? "/drivers"
                                        : it.vehicle_id ? `/vehicles/${it.vehicle_id}`
                                        : "/drivers");
                                return (
                                    <Link
                                        key={it.id}
                                        to={target}
                                        onClick={(e) => { setOpen(false); if (isAlert) markRead(it, null); }}
                                        className="flex items-start gap-3 px-4 py-3 hover:bg-slate-50 transition-colors"
                                        data-testid={`notif-${it.id}`}
                                    >
                                        <div className={`h-8 w-8 rounded-md border flex items-center justify-center shrink-0 ${SEVERITY_STYLES[it.severity]}`}>
                                            <Icon size={14} />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="text-sm font-medium truncate">{it.title}{it.subject ? ` · ${it.subject}` : ""}</div>
                                            <div className="text-xs text-fleet-inkSoft">
                                                {isAlert ? (
                                                    <>
                                                        {it.message}
                                                        {it.created_at && <span className="font-mono ml-1">· {formatDateTime(it.created_at)}</span>}
                                                    </>
                                                ) : (
                                                    <>
                                                        {it.days_left < 0 ? `Expired ${-it.days_left} days ago` : it.days_left === 0 ? "Expires today" : `Expires in ${it.days_left} days`}
                                                        {it.expiry_date && <span className="font-mono"> · {formatDate(it.expiry_date)}</span>}
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                    </Link>
                                );
                            })
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
